import type {
  DtoV1BoltAuditBoltAuditEntity,
  DtoV1BoltAuditBoltAuditResponse,
  DtoV1BoltAuditIncomeReconSectionedBoltAuditResponse,
  DtoV1BoltAuditSectionedBoltAuditResponse,
} from '~/__generated__/api';
import { RULE_CATEGORY_ID, RULE_ID } from '~/common/constants';
import {
  FHA_SECONDARY_FINANCING_RULE_ID,
  FHA_UFMIP_REFUND_RULE_ID,
  UNDERWRITER_FHA_RULE_ID,
} from '~/common/constants/api-ids';
import {
  BusinessAssetTypeArray,
  DepositoryLiquidAssetTypeArray,
  DepositoryNonLiquidAssetTypeArray,
  EMDAssetTypeArray,
  ExpectedAssetTypeArray,
} from '~/pages/Assets/AssetDepositoryAccounts/enum';
import {
  CalculationTypes,
  DocumentTypes,
  IncomeRuleIds as INCOME_RULE_ID,
} from '~/pages/IncomeCalculator/enums';
import type { GutterItem } from '~/types';

import { getVABenefitLetterDocumentTypeId } from './VABenefitLetterHelper';
import {
  AuditAssetTypeMapping,
  AuditHistoryEnityEnum,
  AuditHistoryPageEnum,
  LedgerStyleAuditHistoryKeys,
  PageTitleMap,
} from './constants';

const assetRuleIdWithAuditHistoryPage = [
  RULE_ID.ASSET_TYPE_RULE_ID,
  RULE_ID.ASSET_BANK_STATEMENT_RULE_ID,
  RULE_ID.ASSET_ACCOUNT_SUMMARY_RULE_ID,
  RULE_ID.ASSET_ACCOUNT_REVIEW_RULE_ID,
  RULE_ID.TRANSFER_RECONCILIATION_RULE_ID,
  RULE_ID.ASSET_RECONCILIATION_RULE_ID,
  RULE_ID.ASSET_DEPOSIT_SOURCING_RULE_ID,
];

const assetRuleIds = assetRuleIdWithAuditHistoryPage.reduce((acc, ruleId) => {
  acc[ruleId] = determineAssetAuditHistoryRoute;
  return acc;
}, {});

// TOOD: this is probably not the best name
const AuditHistoryMap: Record<
  number,
  number | typeof determineAssetAuditHistoryRoute
> = {
  [RULE_ID.TITLE_COMMITMENT_RULE]:
    AuditHistoryPageEnum.PROPERTY_TITLE_COMMITMENT,
  [RULE_ID.NET_TANGIBLE_BENEFITS_RULE_ID]:
    AuditHistoryPageEnum.NET_TANGIBLE_BENEFITS,
  [RULE_ID.CPL_REVIEW_RULE]:
    AuditHistoryPageEnum.PROPERTY_CLOSING_PROTECTION_LETTER,
  [RULE_ID.WIRE_INSTRUCTIONS_RULE]:
    AuditHistoryPageEnum.PROPERTY_WIRE_INSTRUCTIONS,
  ...assetRuleIds,
  [RULE_ID.HOMEOWNERS_PARENT_RULE_ID]:
    AuditHistoryPageEnum.HOMEOWNERS_INSURANCE,
  [RULE_ID.PURCHASE_AGREEMENT_RULE_ID]: AuditHistoryPageEnum.CONTRACT_REVIEW,
  [RULE_ID.FIRST_TIME_HOME_BUYER_DU_RULE]:
    AuditHistoryPageEnum.FIRST_TIME_HOME_BUYER,
  [RULE_ID.FIRST_TIME_HOME_BUYER_LPA_RULE]:
    AuditHistoryPageEnum.FIRST_TIME_HOME_BUYER,
  [RULE_ID.BORROW_SMART_RULE]: AuditHistoryPageEnum.FIRST_TIME_HOME_BUYER,
  [UNDERWRITER_FHA_RULE_ID]: AuditHistoryPageEnum.FhaOverview,
  [FHA_UFMIP_REFUND_RULE_ID]: AuditHistoryPageEnum.Ufmip_Refund,
  [FHA_SECONDARY_FINANCING_RULE_ID]: AuditHistoryPageEnum.Secondary_Financing,
  [RULE_ID.RESIDENCY_STATUS_ALL_ALERTS_RULE]:
    AuditHistoryPageEnum.RESIDENCY_STATUS,
  [RULE_ID.FLOOD_PARENT_RULE_ID]: AuditHistoryPageEnum.FLOOD_INSURANCE,
  [RULE_ID.SUPPLEMENTAL_PARENT_RULE_ID]:
    AuditHistoryPageEnum.SUPPLEMENTAL_INSURANCE,
  [RULE_ID.NON_OCCUPYING_CO_BORROWER_RULE_ID]:
    AuditHistoryPageEnum.NON_OCCUPYING_CO_BORROWER,
  [RULE_ID.VA_LOAN_IDENTIFICATION_NUMBER_RULE_ID]:
    AuditHistoryPageEnum.LOAN_IDENTIFICATION_NUMBER,
  [RULE_ID.VA_OBLIGOR_RULE_ID]: AuditHistoryPageEnum.OBLIGOR,
  [RULE_ID.TITLE_SUMMARY_RULE_ID]: AuditHistoryPageEnum.TITLE_SUMMARY_TRAC_PLUS,
  [RULE_ID.VA_CAIVRS_RULE_ID]: AuditHistoryPageEnum.VA_CAIVRS,
  [RULE_ID.VA_DOCUMENT_REVIEW_CREDIT_REPORT_RULE_ID]:
    AuditHistoryPageEnum.VA_CREDIT_REPORT,
  [RULE_ID.VA_DOCUMENT_REVIEW_CREDIT_SUPPLEMENT_RULE_ID]:
    AuditHistoryPageEnum.VA_CREDIT_SUPPLEMENT,
  [RULE_ID.VA_DOCUMENT_REVIEW_FORBEARANCE_REVIEW_RULE_ID]:
    AuditHistoryPageEnum.VA_FORBEARANCE_REVIEW,
  [RULE_ID.VA_DOCUMENT_REVIEW_NOTE_RULE_ID]: AuditHistoryPageEnum.VA_Note,
  [RULE_ID.VA_DOCUMENT_REVIEW_MORTGAGE_STATEMENT_RULE_ID]:
    AuditHistoryPageEnum.VA_MORTGAGE_STATEMENT,
  [RULE_ID.VA_DOCUMENT_REVIEW_PAYOFF_STATEMENT_RULE_ID]:
    AuditHistoryPageEnum.VA_PAYOFF_STATEMENT,
  [RULE_ID.VA_DOCUMENT_REVIEW_LOAN_MODIFICATION_AGREEMENT_RULE_ID]:
    AuditHistoryPageEnum.VA_LOANMODIFICATION_AGREEMENT,
};

const AppraisalAuditHistoryMap: Record<number, number> = {
  [RULE_ID.APPRAISALS_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_ASSIGNMENT_INFORMATION,
  [RULE_ID.APPRAISALS_SUBJECT_PROPERTY_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_SUBJECT_PROPERTY,
  [RULE_ID.APPRAISALS_SITE_RULE_ID]: AuditHistoryPageEnum.APPRAISAL_SITE,
  [RULE_ID.APPRAISALS_ENERGY_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_ENERGY_EFFICIENT_AND_GREEN_FEATURES,
  [RULE_ID.APPRAISALS_SKETCH_RULE_ID]: AuditHistoryPageEnum.APPRAISAL_SKETCH,
  [RULE_ID.APPRAISALS_DWELLING_EXTERIOR_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_DWELLING_EXTERIOR,
  [RULE_ID.APPRAISALS_MANUFACTURED_HOME_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_MANUFACTURED_HOME,
  [RULE_ID.APPRAISALS_UNIT_INTERIOR_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_UNIT_INTERIOR,
  [RULE_ID.APPRAISALS_FUNCTIONAL_OBSOLESCENCE_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_FUNCTIONAL_OBSOLESCENCE,
  [RULE_ID.APPRAISALS_OUTBUILDING_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_OUTBUILDING,
  [RULE_ID.APPRAISALS_VEHICLE_STORAGE_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_VEHICLE_STORAGE,
  [RULE_ID.APPRAISALS_SUBJECT_PROPERTY_AMENITIES_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_SUBJECT_PROPERTY_AMENITIES,
  [RULE_ID.APPRAISALS_PROJECT_INFORMATION_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_PROJECT_INFORMATION,
  [RULE_ID.APPRAISALS_SALES_CONTRACT_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_SALES_CONTRACT,
  [RULE_ID.APPRAISALS_SALES_COMPARISON_APPROACH_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_SALES_COMPARISONS_APPROACH,
  [RULE_ID.APPRAISALS_REMOVE_APPRAISAL_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_REMOVE_APPRAISAL,
  [RULE_ID.APPRAISALS_HIGHEST_AND_BEST_USE_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_HIGHEST_AND_BEST_USE,
  [RULE_ID.APPRAISALS_MARKET_RULE_ID]: AuditHistoryPageEnum.APPRAISAL_MARKET,
  [RULE_ID.APPRAISALS_COST_APPROACH_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_COST_APPROACH,
  [RULE_ID.APPRAISALS_SUBJECT_LISTING_INFO_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_SUBJECT_LISTING_INFORMATION,
  [RULE_ID.APPRAISALS_FHA_REQUIREMENTS_RULE_ID]:
    AuditHistoryPageEnum.APPRAISAL_FHA_REQUIREMENTS,
};

// Gutters that can fire more than once pass the instance identifier so the audit
// page only shows that instance's namespaced photo/damage answers.
const AppraisalInstanceAuditReferences: Record<
  number,
  { queryParam: string; additionalReference: string }
> = {
  [RULE_ID.APPRAISALS_UNIT_INTERIOR_RULE_ID]: {
    queryParam: 'unitIdentifier',
    additionalReference: 'UnitIdentifier',
  },
  [RULE_ID.APPRAISALS_DWELLING_EXTERIOR_RULE_ID]: {
    queryParam: 'structureIdentifier',
    additionalReference: 'UnitIdentifier',
  },
  [RULE_ID.APPRAISALS_OUTBUILDING_RULE_ID]: {
    queryParam: 'outbuildingType',
    additionalReference: 'OutbuildingType',
  },
};

function determineAppraisalAuditPage(
  selectedAlert: GutterItem,
  pageEnum: number,
) {
  const additionalReferences = selectedAlert.additionalReferences ?? {};
  const queryParams = new URLSearchParams({
    appraiserLicenseNumber: additionalReferences.AppraiserLicenseNumber ?? '',
  });
  const instanceReference =
    AppraisalInstanceAuditReferences[selectedAlert.ruleId];
  if (instanceReference) {
    queryParams.set(
      instanceReference.queryParam,
      additionalReferences[instanceReference.additionalReference] ?? '',
    );
  }
  return `${pageEnum}?${queryParams.toString()}`;
}

const AppraisalAuditPages = new Set(Object.values(AppraisalAuditHistoryMap));

export function isAppraisalAuditPage(pageEnum?: string) {
  return AppraisalAuditPages.has(Number(pageEnum));
}

export type AppraisalAuditGutterGroup = {
  label: string;
  options: { label: string; value: string }[];
};

function getRootGutter(gutter: GutterItem) {
  let root = gutter;
  while (root.parent) {
    root = root.parent;
  }
  return root;
}

// Groups every Appraisal gutter under its appraisal, the same way the gutter rail does.
export function getAppraisalAuditGutterGroups(
  gutters: GutterItem[],
): AppraisalAuditGutterGroup[] {
  const groups = new Map<GutterItem, AppraisalAuditGutterGroup>();
  const seenPages = new Set<string>();

  for (const gutter of gutters) {
    const pageEnum = AppraisalAuditHistoryMap[gutter.ruleId];
    const auditPage = pageEnum && determineAppraisalAuditPage(gutter, pageEnum);
    if (!auditPage || seenPages.has(auditPage)) {
      continue;
    }
    seenPages.add(auditPage);

    const root = getRootGutter(gutter);
    const group = groups.get(root) ?? { label: root.displayTitle, options: [] };
    group.options.push({
      label: gutter.displayTitle || PageTitleMap[pageEnum],
      value: auditPage,
    });
    groups.set(root, group);
  }

  return [...groups.values()];
}

function determineMasterInsuranceAuditPage(selectedAlert: GutterItem) {
  return selectedAlert?.ruleReference?.id === -2
    ? AuditHistoryPageEnum.MASTER_NO_DOCS_INSURANCE
    : AuditHistoryPageEnum.MASTER_INSURANCE;
}

function determineAssetAuditHistoryRoute(selectedAlert: GutterItem) {
  const ruleId = selectedAlert?.ruleId;
  const assetType = selectedAlert?.additionalReferences?.AssetType;
  const isDepositoryOrBusinessAsset = [
    ...DepositoryLiquidAssetTypeArray,
    ...DepositoryNonLiquidAssetTypeArray,
    ...BusinessAssetTypeArray,
  ].includes(assetType);

  let id = selectedAlert.ruleReference?.id;
  const documentId = selectedAlert?.ruleReference?.id;

  if (!assetRuleIdWithAuditHistoryPage.includes(ruleId))
    return AuditHistoryPageEnum.UNKNOWN;

  if (ruleId === RULE_ID.ASSET_BANK_STATEMENT_RULE_ID) {
    id = +selectedAlert?.additionalReferences?.AssetId;
  }
  const assetTypeId = Object.keys(AuditAssetTypeMapping).find(
    (key) => AuditAssetTypeMapping[key] === assetType,
  );

  switch (ruleId) {
    case RULE_ID.ASSET_TYPE_RULE_ID:
      if (isDepositoryOrBusinessAsset) {
        return `assets/${id}/${assetTypeId}`;
      } else if (EMDAssetTypeArray.includes(assetType)) {
        return `${AuditHistoryPageEnum.ASSET_EMD}/${id}/${AuditHistoryEnityEnum.ASSET}`;
      } else if (ExpectedAssetTypeArray.includes(assetType)) {
        return `${AuditHistoryPageEnum.ASSET_EXPECTED_ASSETS}/${id}/${AuditHistoryEnityEnum.ASSET}`;
      }
      break;
    case RULE_ID.ASSET_BANK_STATEMENT_RULE_ID:
      return `assets/bankStatement/${id}/${documentId}`;
    case RULE_ID.ASSET_ACCOUNT_SUMMARY_RULE_ID:
      return `${AuditHistoryPageEnum.ASSET_ACCOUNT_SUMMARY}/${id}/${AuditHistoryEnityEnum.ASSET}`;
    case RULE_ID.ASSET_ACCOUNT_REVIEW_RULE_ID:
      return `${AuditHistoryPageEnum.ASSET_ACCOUNT_REVIEW}/${id}/${AuditHistoryEnityEnum.ASSET}`;
    case RULE_ID.TRANSFER_RECONCILIATION_RULE_ID:
      return `transfer-reconciliation/${AuditHistoryPageEnum.TRANSFER_RECONCILIATION}`;
    case RULE_ID.ASSET_DEPOSIT_SOURCING_RULE_ID:
      return `assets/${id}/${assetTypeId}/${AuditHistoryPageEnum.DEPOSIT_SOURCING_PAGE}`;
    case RULE_ID.ASSET_RECONCILIATION_RULE_ID:
      return `asset-reconciliation/${AuditHistoryPageEnum.ASSET_RECONCILIATION}`;
    default:
      return AuditHistoryPageEnum.UNKNOWN;
  }
  const assetTypeMapping = {
    'Gift of Equity': {
      pageEnum: AuditHistoryPageEnum.ASSET_GIFT_OF_EQUITY,
      entityEnum: AuditHistoryEnityEnum.ASSET_GIFT_GOE_ENTITY,
    },
    Grant: {
      pageEnum: AuditHistoryPageEnum.ASSET_GRANT,
      entityEnum: AuditHistoryEnityEnum.ASSET,
    },
    'Net Proceeds': {
      pageEnum: AuditHistoryPageEnum.ASSET_NET_PROCEEDS,
      entityEnum: AuditHistoryEnityEnum.ASSET,
    },
    Gift: {
      pageEnum: AuditHistoryPageEnum.ASSET_GIFT,
      entityEnum: AuditHistoryEnityEnum.ASSET_GIFT_GOE_ENTITY,
    },
    'Cash Deposit': {
      pageEnum: AuditHistoryPageEnum.ASSET_CASH_DEPOSIT,
      entityEnum: AuditHistoryEnityEnum.ASSET,
    },
    'Expected Assets': {
      pageEnum: AuditHistoryPageEnum.ASSET_EXPECTED_ASSETS,
      entityEnum: AuditHistoryEnityEnum.ASSET,
    },
  };

  if (assetTypeMapping[assetType]) {
    const { pageEnum, entityEnum } = assetTypeMapping[assetType];
    return `${pageEnum}/${id}/${entityEnum}`;
  }
  return `${AuditHistoryPageEnum.ASSET_EMD}/${id}/${AuditHistoryEnityEnum.ASSET}`;
}

function getScheduleCPage(additionalRefs: GutterItem['additionalReferences']) {
  return additionalRefs.IsScheduleCBoltClearable === 'True'
    ? AuditHistoryPageEnum.INCOME_VALIDATE_SCHEDULE_C_V2
    : AuditHistoryPageEnum.INCOME_VALIDATE_SCHEDULE_C;
}

function getScheduleK11120SPage(
  additionalRefs: GutterItem['additionalReferences'],
) {
  return additionalRefs.Is1120sBoltClearable === 'True'
    ? AuditHistoryPageEnum.INCOME_VALIDATE_1120S_SCHEDULE_K_1S_V2
    : AuditHistoryPageEnum.INCOME_VALIDATE_1120S_SCHEDULE_K_1S;
}

function getForm1065Page(
  additionalRefs: GutterItem['additionalReferences'],
  isForm1065: boolean,
) {
  if (additionalRefs.Is1065NewUIAndConditionsBoltClearable !== 'True') {
    return AuditHistoryPageEnum.UNKNOWN;
  }
  return isForm1065
    ? AuditHistoryPageEnum.INCOME_VALIDATE_FORM_1065
    : AuditHistoryPageEnum.INCOME_VALIDATE_1065_SCHEDULE_K_1;
}

function getForm1120SPage(additionalRefs: GutterItem['additionalReferences']) {
  return additionalRefs.Is1120sBoltClearable === 'True'
    ? AuditHistoryPageEnum.INCOME_VALIDATE_1120S_V2
    : AuditHistoryPageEnum.INCOME_VALIDATE_1120S;
}

function getForm1120Page(additionalRefs: GutterItem['additionalReferences']) {
  return additionalRefs.Is1120NewUIAndConditionsBoltClearable === 'True'
    ? AuditHistoryPageEnum.INCOME_VALIDATE_Form1120_V2
    : AuditHistoryPageEnum.INCOME_VALIDATE_1120;
}

function getVABenefitsBankStatementPage(
  additionalRefs: GutterItem['additionalReferences'],
) {
  return additionalRefs.IsVABenefitsIncomeWorkflowEnabled === 'True'
    ? AuditHistoryPageEnum.INCOMEVALIDATE_VABENEFITS_BANKSTATEMENT
    : AuditHistoryPageEnum.UNKNOWN;
}

function getVABenefitLetterPage(
  additionalRefs: GutterItem['additionalReferences'],
) {
  return additionalRefs.IsVABenefitsIncomeWorkflowEnabled === 'True'
    ? AuditHistoryPageEnum.INCOMEVALIDATE_VABENEFITS_BENEFITLETTER
    : AuditHistoryPageEnum.UNKNOWN;
}

function determineIncome(selectedAlert: GutterItem) {
  const customerRecordId = +(selectedAlert?.customerRecordId ?? 0);
  const employerId = +(selectedAlert?.ruleReference?.id ?? 0);
  const additionalRefs = selectedAlert?.additionalReferences ?? {};
  const ruleId = selectedAlert?.ruleId;
  const paths = {
    SmartValidateDocument: 'income/validateDocument',
    SmartCalculateIncome: 'income/calculate',
    Default: 'income',
  };

  if (
    ruleId === INCOME_RULE_ID.SmartValidateDocument &&
    additionalRefs.IsPaystubDeductionsNewFlowEnabled === 'False'
  ) {
    const documentId = +(additionalRefs.DocumentId ?? 0);
    return `${paths.SmartValidateDocument}/${customerRecordId}/${employerId}/${documentId}`;
  }

  const calculatorTypeId = +(additionalRefs.CalculatorTypeId ?? 0);
  const smartCalculateTypes: number[] = [
    CalculationTypes.PayStub,
    CalculationTypes.YearEndPaystub,
  ];
  if (
    ruleId === INCOME_RULE_ID.CalculateIncome &&
    smartCalculateTypes.includes(calculatorTypeId)
  ) {
    return `${paths.SmartCalculateIncome}/${customerRecordId}/${employerId}/${calculatorTypeId}`;
  }

  if (
    ruleId === INCOME_RULE_ID.CalculateIncome &&
    additionalRefs.IsV3Active === 'True'
  ) {
    const queryParams = new URLSearchParams();
    switch (selectedAlert.ruleReference?.type) {
      case 'employer':
        queryParams.set(
          'customerRecordId',
          selectedAlert.customerRecordId.toString(),
        );
        queryParams.set(
          'employerId',
          selectedAlert.ruleReference?.id.toString(),
        );
        break;

      case 'address':
        queryParams.set(
          'addressId',
          selectedAlert.ruleReference?.id.toString(),
        );
        break;
    }

    return `${paths.SmartCalculateIncome}/${additionalRefs.CalculatorType}?${queryParams.toString()}`;
  }

  const { DocumentTypeId, DocumentId, CalculationId, CalculatorTypeId } =
    additionalRefs;

  const applicableIncomeAuditType = Number.parseInt(
    (DocumentTypeId ?? CalculatorTypeId ?? 0).toString(),
  );

  const auditPageMapping = {
    [DocumentTypes.SocialSecurityAwardLetter]:
      AuditHistoryPageEnum.INCOME_VALIDATE_SOCIAL_SECURITY_AWARD_LETTER,
    [DocumentTypes.ScheduleC]: getScheduleCPage(additionalRefs),
    [CalculationTypes.SocialSecurityAwardLetter]:
      AuditHistoryPageEnum.CALCULATE_SOCIAL_SECURITY_AWARD_LETTER,
    [CalculationTypes.ScheduleC]: AuditHistoryPageEnum.CALCULATE_SCHEDULE_C,
    [DocumentTypes.ScheduleK1Form1120s]: getScheduleK11120SPage(additionalRefs),
    [DocumentTypes.Form1120S]: getForm1120SPage(additionalRefs),
    [DocumentTypes.Form1120]: getForm1120Page(additionalRefs),
    [DocumentTypes.W2]: AuditHistoryPageEnum.INCOME_VALIDATE_W2,
    [DocumentTypes.PayStub]: AuditHistoryPageEnum.INCOME_VALIDATE_PAYSTUB,
    [CalculationTypes.Corporation]: AuditHistoryPageEnum.CALCULATE_1120,
    [CalculationTypes.SoleProprietorship]: AuditHistoryPageEnum.CALCULATE_1120S,
    [CalculationTypes.FutureIncome]: AuditHistoryPageEnum.FUTURE_INCOME,
    [CalculationTypes.Partnership]: AuditHistoryPageEnum.CALCULATE_1065,
    [DocumentTypes.Form1065]: getForm1065Page(additionalRefs, true),
    [DocumentTypes.ScheduleK1Form1065]: getForm1065Page(additionalRefs, false),
    [DocumentTypes.BankStatement]:
      getVABenefitsBankStatementPage(additionalRefs),
    [getVABenefitLetterDocumentTypeId()]:
      getVABenefitLetterPage(additionalRefs),
  };

  const isDeductions = selectedAlert.displayTitle === 'Deductions';
  const isIncomeReconciliation =
    selectedAlert.displayTitle === 'Income Reconciliation';
  let boltAuditPage;
  if (isDeductions) {
    boltAuditPage = AuditHistoryPageEnum.INCOME_DEDUCTIONS;
  } else if (isIncomeReconciliation) {
    boltAuditPage = AuditHistoryPageEnum.INCOME_RECONCILIATION;
  } else if (ruleId === INCOME_RULE_ID.RecentRaise) {
    boltAuditPage = AuditHistoryPageEnum.RECENT_RAISE;
  } else {
    boltAuditPage =
      auditPageMapping[applicableIncomeAuditType] ??
      AuditHistoryPageEnum.UNKNOWN;
  }

  let entity: number;
  if (
    ruleId === INCOME_RULE_ID.ValidateDocument ||
    ruleId === AuditHistoryEnityEnum.PAYSTUB
  ) {
    entity = AuditHistoryEnityEnum.INCOME_DOCUMENT;
  } else if (
    [
      INCOME_RULE_ID.CalculateIncome,
      INCOME_RULE_ID.FutureIncome,
      INCOME_RULE_ID.RecentRaise,
    ].includes(ruleId)
  ) {
    entity = AuditHistoryEnityEnum.INCOME_CALCULATION;
  } else {
    entity = 0;
  }

  const applicableIncomeAuditId = +(DocumentId ?? CalculationId ?? 0);
  return `${paths.Default}/${entity}/${boltAuditPage}/${applicableIncomeAuditId}/${applicableIncomeAuditType}/${employerId}/${customerRecordId}`;
}

function appendAuditSearchParams(
  pageId: number,
  searchParams?: URLSearchParams,
): number | string {
  const filteredSearchParams = new URLSearchParams();

  searchParams?.forEach((value, key) => {
    if (['ruleId', 'insuranceDocumentType'].includes(key)) {
      filteredSearchParams.append(key, value);
    }
  });

  return filteredSearchParams.size
    ? `${pageId}?${filteredSearchParams.toString()}`
    : pageId;
}

export function getAuditHistoryPageId(
  selectedAlert?: GutterItem,
  searchParams?: URLSearchParams,
) {
  const { ruleId, ruleCategoryId } = selectedAlert ?? {};
  const incomeRuleIds = [
    INCOME_RULE_ID.SmartValidateDocument,
    INCOME_RULE_ID.ValidateDocument,
    INCOME_RULE_ID.CalculateIncome,
    INCOME_RULE_ID.Deductions,
    INCOME_RULE_ID.FutureIncome,
    INCOME_RULE_ID.RecentRaise,
    INCOME_RULE_ID.IncomeReconciliation,
  ];

  if (
    incomeRuleIds.includes(ruleId) &&
    ruleCategoryId === RULE_CATEGORY_ID.INCOME
  ) {
    return determineIncome(selectedAlert);
  }

  const appraisalAuditPage = AppraisalAuditHistoryMap[ruleId];
  if (appraisalAuditPage) {
    return determineAppraisalAuditPage(selectedAlert, appraisalAuditPage);
  }

  const mapResult = AuditHistoryMap[ruleId] ?? AuditHistoryPageEnum.UNKNOWN;

  switch (ruleId) {
    case RULE_ID.TAX_SUMMARY_RULE_ID:
      return `sectioned/${AuditHistoryPageEnum.PROPERTY_TAX_SUMMARY}/${AuditHistoryEnityEnum.PROPERTY}`;
    case RULE_ID.TAX_BREAKDOWN_RULE_ID:
      return `sectioned/${AuditHistoryPageEnum.PROPERTY_TAX_BREAKDOWN}/${AuditHistoryEnityEnum.PROPERTY}`;
    case RULE_ID.TAX_BREAKDOWN_NO_DOCS_RULE_ID:
      return `sectioned/${AuditHistoryPageEnum.PROPERTY_TAX_BREAKDOWN}/${AuditHistoryEnityEnum.PROPERTY}`;
    case RULE_ID.TAX_ESCROW_DETAILS_RULE_ID:
      return `sectioned/${AuditHistoryPageEnum.PROPERTY_TAX_ESCROW_DETAILS}/${AuditHistoryEnityEnum.PROPERTY}?taxId=${selectedAlert.additionalReferences['TaxId']}`;
    case RULE_ID.STATUS_AND_REVIEW_FLAG_RULE_ID:
      return 'status-and-review';
    case RULE_ID.RESIDENCY_STATUS_ALL_ALERTS_RULE:
      return `${AuditHistoryPageEnum.RESIDENCY_STATUS}/${selectedAlert.customerRecordId}/${AuditHistoryEnityEnum.BORROWER}`;
    case RULE_ID.SSN_MISMATCH_ALL_ALERTS_RULE:
      return `${AuditHistoryPageEnum.FHASSN_MISMATCH}/${selectedAlert.customerRecordId}/${AuditHistoryEnityEnum.BORROWER}`;
    case RULE_ID.COMMUNITY_PROPERTY_STATE_RULE:
      return `${AuditHistoryPageEnum.COMMUNITY_PROPERTY_STATE}/${selectedAlert.customerRecordId}/${AuditHistoryEnityEnum.BORROWER}`;
    case RULE_ID.NON_OCCUPYING_CO_BORROWER_RULE_ID:
      return `${AuditHistoryPageEnum.NON_OCCUPYING_CO_BORROWER}`;
    case RULE_ID.DEROGATORY_EVENTS_PARENT_RULE:
      return `${AuditHistoryPageEnum.DEROGATORY_CREDIT_EVENTS}`;
    case RULE_ID.FRAUD_ALERT_PARENT_RULE:
      return `${AuditHistoryPageEnum.FRAUD_ALERT}/${selectedAlert.customerRecordId}/${AuditHistoryEnityEnum.BORROWER}`;
    case RULE_ID.VA_LIABILITY_MORTGAGE_SEASONING_PARENT_RULE:
      return `${AuditHistoryPageEnum.VA_MORTGAGE_SEASONING}`;
    case RULE_ID.VA_LIABILITY_MORTGAGE_HISTORY_PARENT_RULE:
      return `${AuditHistoryPageEnum.VA_MORTGAGE_HISTORY}`;
    case RULE_ID.NET_TANGIBLE_BENEFITS_VA_IRRRL_RULE_ID:
      return `${AuditHistoryPageEnum.VA_NET_TANGIBLE_BENEFITS}`;
    case RULE_ID.LIABILITIES_MORTGAGE_HISTORY_RULE_ID:
      return `${AuditHistoryPageEnum.LIABILITIES_MORTGAGE_HISTORY}`;
    case RULE_ID.NEW_CONSTRUCTION_RULE_ID:
      return `${AuditHistoryPageEnum.PROPERTY_NEW_CONSTRUCTION_REQUIREMENTS}`;
  }

  if (ruleId === RULE_ID.MASTER_INSURANCE_RULE_ID) {
    return appendAuditSearchParams(
      determineMasterInsuranceAuditPage(selectedAlert),
      searchParams,
    );
  }

  if (typeof mapResult === 'number') {
    return appendAuditSearchParams(mapResult, searchParams);
  }

  // INFO: if/when there are a few conditions for ruleIds, turn this into a switch statement
  if (assetRuleIdWithAuditHistoryPage.includes(ruleId)) {
    return mapResult(selectedAlert);
  }

  return mapResult(selectedAlert);
}

export function transformBoltAuditEndpointData(
  responseData: DtoV1BoltAuditBoltAuditResponse,
) {
  return {
    ...responseData,
    entitiesAudit: (responseData.entitiesAudit ?? []).map((entity) => {
      return {
        ...entity,
        fieldsAudit: entity.fieldsAudit?.map(transformField),
      };
    }),
  };
}

function transformFieldValue(fieldValue: { fieldValue?: string }) {
  const fieldValueRows = fieldValue.fieldValue?.split('\n').map((row) => {
    if (row.length === 0) {
      return null;
    }

    const [label, value] = row.split(':');

    return {
      label,
      value: value.trim(),
    };
  });

  return {
    ...fieldValue,
    fieldValue: {
      type: 'ledger' as const,
      rows: fieldValueRows,
    },
  };
}

function transformField(field: any) {
  if (LedgerStyleAuditHistoryKeys.includes(field.key)) {
    return {
      key: field.key,
      value: field.value?.map(transformFieldValue),
    };
  }

  return field;
}

function transformSection(section: any) {
  return {
    ...section,
    fields: section.fields?.map(transformField),
  };
}

function transformEntity(entity: any) {
  return {
    ...entity,
    auditSections: entity.auditSections?.map(transformSection),
  };
}

export function transformIncomeReconBoltAuditEndpointData(
  responseData: DtoV1BoltAuditIncomeReconSectionedBoltAuditResponse,
) {
  return {
    ...responseData,
    entity: responseData.entity?.map(transformEntity),
  };
}

export function transformSectionedBoltAuditEndpointData(
  responseData: DtoV1BoltAuditSectionedBoltAuditResponse,
) {
  return {
    ...responseData,
    entity: responseData.entity?.map((entity) => ({
      ...entity,
      auditSections: entity.auditSections?.map(transformSection),
    })),
  };
}

export function convertAuditEntityToAuditTableField(
  entity: DtoV1BoltAuditBoltAuditEntity,
) {
  return (entity.entityFields ?? []).map((field) => ({
    ...field,
    value: (field.value ?? []).map((value) => ({
      ...value,
      fieldValue: Array.isArray(value.fieldValue)
        ? value.fieldValue.join(', ')
        : value.fieldValue,
    })),
  }));
}
